/**
 * Tri des listes de l'espace pro — pendant du filtrage (`./utils`).
 *
 * Une `SortConfig` par liste (déclarée dans le fichier de son domaine, à côté de ses `*_FILTERS`)
 * pilote à la fois : la liste blanche + le `orderBy` Prisma côté backend, la doc Swagger (donc les
 * types Orval et les paramètres des tools MCP/Pulse) et le menu « Trier » côté front.
 *
 * Transport : deux paramètres plats `sort_by` / `sort_dir`. Leur ABSENCE = ordre par défaut de la
 * liste (l'ordre historique) : choisir l'option par défaut dans l'UI retire donc les paramètres de
 * l'URL. Toute valeur inconnue retombe silencieusement sur le défaut (même précédent que
 * `generatePrismaFilters` : jamais de 400).
 *
 * ⚠️ Ce fichier ne doit importer AUCUN fichier de domaine : ce sont eux qui l'importent (sinon
 * import circulaire via `professional.ts`).
 */

/** Sens de tri. Valeurs identiques à `Prisma.SortOrder` (mapping 1:1 côté backend). */
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export const SORT_BY_PARAM = 'sort_by';
export const SORT_DIR_PARAM = 'sort_dir';
export const SORT_PARAM_KEYS = [SORT_BY_PARAM, SORT_DIR_PARAM] as const;

/**
 * Pilote les libellés de sens dans l'UI. `fixed` = préréglage à sens unique (ordre personnalisé
 * par glisser-déposer, ou ordre composite historique « Par défaut ») : aucun sens inverse proposé.
 */
export type SortValueType = 'date' | 'text' | 'number' | 'rating' | 'fixed';

export interface SortFieldConfig {
  /** Clé de traduction (namespace DYNAMIC_FORM) du critère, ex. `SORT_FIELD_CREATED_AT`. */
  label: string;
  value_type: SortValueType;
  default_direction: SortDirection;
  /**
   * Entrées `orderBy` métier du critère (objets au format Prisma : le shared reste sans Prisma).
   * N'inclut PAS le tiebreaker, sauf s'il fait partie d'un ordre composite historique. Renvoie un
   * tableau NEUF à chaque appel (l'appelant peut le muter en ajoutant le tiebreaker).
   */
  order_by: (direction: SortDirection) => Record<string, unknown>[];
  /** Description (FR) pour Swagger / MCP (Pulse) : sens métier du critère + gestion des valeurs vides. */
  api_description: string;
}

export interface SortConfig {
  /** Ordre d'insertion = ordre du menu ; le critère par défaut vient EN PREMIER. */
  fields: Record<string, SortFieldConfig>;
  /** Doit être une clé de `fields`. */
  default_field: string;
  /** Clé primaire unique du modèle listé, ajoutée en dernier par le backend (pagination déterministe). */
  tiebreaker: string;
}

export interface ResolvedSort {
  by: string;
  dir: SortDirection;
  is_default: boolean;
}

/** Entrée `orderBy` « valeurs vides en dernier », quel que soit le sens (colonnes nullables). */
export const sortNullsLast = (direction: SortDirection): { sort: SortDirection; nulls: 'last' } => ({
  sort: direction,
  nulls: 'last',
});

const oppositeSortDirection = (direction: SortDirection): SortDirection =>
  direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;

/** Sens autorisés d'un critère : `fixed` → [sens par défaut] ; sinon [sens par défaut, sens inverse]. */
export const getSortFieldDirections = (field: SortFieldConfig): SortDirection[] =>
  field.value_type === 'fixed'
    ? [field.default_direction]
    : [field.default_direction, oppositeSortDirection(field.default_direction)];

/** Un paramètre répété dans l'URL (`?sort_by=a&sort_by=b`) arrive en tableau : on garde le premier. */
const firstParamValue = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);

/**
 * Valide les paramètres de tri contre la config (liste blanche). Accepte n'importe quel objet de
 * paramètres (filtres d'URL, query, filtres d'un bulk `all_matching`). `hasOwnProperty` et non `in` :
 * une clé du prototype (`constructor`, `__proto__`…) ne doit jamais être prise pour un critère.
 */
export const resolveSort = (params: Record<string, unknown> | undefined | null, config: SortConfig): ResolvedSort => {
  const rawBy = firstParamValue(params?.[SORT_BY_PARAM]);
  const by =
    typeof rawBy === 'string' && Object.prototype.hasOwnProperty.call(config.fields, rawBy)
      ? rawBy
      : config.default_field;
  const field = config.fields[by];

  const rawDir = firstParamValue(params?.[SORT_DIR_PARAM]);
  const allowedDirections = getSortFieldDirections(field);
  const dir = allowedDirections.find((direction: SortDirection): boolean => direction === rawDir);
  const resolvedDir = dir ?? field.default_direction;

  return {
    by,
    dir: resolvedDir,
    is_default: by === config.default_field && resolvedDir === config.fields[config.default_field].default_direction,
  };
};

/** Option du menu « Trier » : un critère × un sens autorisé. */
export interface SortOption {
  /** `${by}:${dir}` — identifiant stable de l'option (valeur du radio group). */
  value: string;
  by: string;
  dir: SortDirection;
  is_default: boolean;
  /** Clé DYNAMIC_FORM du critère. */
  field_label: string;
  /** Clé DYNAMIC_FORM du sens — absente pour un critère `fixed`. */
  direction_label?: string;
}

/** Libellés (clés DYNAMIC_FORM) des sens, par type de valeur. */
export const SORT_DIRECTION_LABELS: Record<Exclude<SortValueType, 'fixed'>, Record<SortDirection, string>> = {
  date: { [SortDirection.DESC]: 'SORT_DIRECTION_DATE_DESC', [SortDirection.ASC]: 'SORT_DIRECTION_DATE_ASC' },
  text: { [SortDirection.ASC]: 'SORT_DIRECTION_TEXT_ASC', [SortDirection.DESC]: 'SORT_DIRECTION_TEXT_DESC' },
  number: { [SortDirection.DESC]: 'SORT_DIRECTION_NUMBER_DESC', [SortDirection.ASC]: 'SORT_DIRECTION_NUMBER_ASC' },
  rating: { [SortDirection.DESC]: 'SORT_DIRECTION_RATING_DESC', [SortDirection.ASC]: 'SORT_DIRECTION_RATING_ASC' },
};

/** Options du menu, à plat (critères dans l'ordre de déclaration × sens autorisés), option par défaut en tête. */
export const buildSortOptions = (config: SortConfig): SortOption[] => {
  const options = Object.entries(config.fields).flatMap(([by, field]: [string, SortFieldConfig]): SortOption[] =>
    getSortFieldDirections(field).map((dir: SortDirection): SortOption => {
      const valueType = field.value_type;
      return {
        value: `${by}:${dir}`,
        by,
        dir,
        is_default: by === config.default_field && dir === field.default_direction,
        field_label: field.label,
        ...(valueType === 'fixed' ? {} : { direction_label: SORT_DIRECTION_LABELS[valueType][dir] }),
      };
    }),
  );
  return [
    ...options.filter((option: SortOption): boolean => option.is_default),
    ...options.filter((option: SortOption): boolean => !option.is_default),
  ];
};

/** Copie des paramètres SANS les clés de tri (ex. valeur passée aux filtres, calcul « filtres actifs »). */
export const omitSortParams = (params: Record<string, unknown> | undefined | null): Record<string, unknown> => {
  const rest: Record<string, unknown> = { ...(params ?? {}) };
  for (const key of SORT_PARAM_KEYS) delete rest[key];
  return rest;
};

/** Copie des SEULES clés de tri présentes (ex. pour les réappliquer après un changement de filtres). */
export const pickSortParams = (params: Record<string, unknown> | undefined | null): Record<string, unknown> => {
  const picked: Record<string, unknown> = {};
  for (const key of SORT_PARAM_KEYS) {
    if (params?.[key] !== undefined) picked[key] = params[key];
  }
  return picked;
};
